import { personName } from '../../personName';
import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import Box from '@mui/joy/Box';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import Tooltip from '@mui/joy/Tooltip';

import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';

import { format, formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';

import { API_URL } from '../../api';
import { chatMediaUrl, freshChatAttachment, openChatAttachment } from '../../utils/chatMedia';
import ChatMediaPhoto from './ChatMediaPhoto';
import VoiceMessage from './VoiceMessage';
import ImageViewer from './ImageViewer';
import SystemMessage from './SystemMessage';
import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded';

import { CHAT, MOTION, bubbleTime, ownBubble, otherBubble, iconColor } from './chatTheme';

// Реакция по двойному нажатию. Сердце — привычка из мессенджеров;
// список допустимых эмодзи задан на сервере (MessageReaction.EMOJI_CHOICES).
const QUICK_REACTION = '❤️';

// Оставлено ради обратной совместимости: функцию импортируют другие модули.
export const formatMessageDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  if (diff < 24 * 60 * 60 * 1000) {
    return formatDistanceToNow(date, { addSuffix: true, locale: ru });
  }
  if (diff < 7 * 24 * 60 * 60 * 1000) {
    return format(date, 'EEEE в HH:mm', { locale: ru });
  }
  try {
    return format(date, 'dd.MM.yyyy в HH:mm', { locale: ru });
  } catch {
    return null;
  }
};

/**
 * Ширина «дырки», которую текст оставляет под время в правом нижнем углу.
 *
 * Раньше это было жёсткое 52px под «17:15» с галочкой. Когда сообщение
 * правили, перед временем появлялась пометка «изм.» — блок становился шире
 * дырки и наезжал на последнюю строку текста. Считаем ширину по тому, что
 * реально нарисуем.
 */
const timeSlotWidth = ({ isYou, isEdited, isPinned }) => (
  36                      // «17:15» при fontSize 10.5
  + (isYou ? 18 : 0)      // галочки
  + (isEdited ? 32 : 0)   // пометка «изм.» и отступ
  + (isPinned ? 15 : 0)   // скрепка закреплённого
  + 8                     // воздух до правого края
);

/**
 * Пузырь сообщения.
 *
 * Что изменилось:
 *  • время и галочки переехали ВНУТРЬ пузыря, в правый нижний угол — текст
 *    аккуратно обтекает их, как в привычных мессенджерах. Раньше время висело
 *    отдельной строкой под пузырём и ломало ритм ленты;
 *  • имя отправителя внутри пузыря и только там, где оно нужно (групповой
 *    чат, первое сообщение в серии);
 *  • у пузыря появился «хвостик» — он рисуется только у последнего сообщения
 *    в серии, остальные скругляются одинаково;
 *  • картинки показываются плиткой без рамки и подписи-мусора;
 *  • системные события — отдельные строки с выравниванием по левому краю.
 */
export default function ChatBubble({
  message,
  isYou,
  onReply,
  onContextMenu,     // (event, message) — правая кнопка / долгое нажатие
  onReact,           // (message, emoji) — клик по уже стоящей реакции
  showName = false,
  groupPos = 'single',   // single | first | middle | last
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [dragX, setDragX] = useState(0);
  // Индекс открытой в просмотрщике фотографии; null — просмотрщик закрыт
  const [viewerAt, setViewerAt] = useState(null);
  const [mediaError, setMediaError] = useState('');
  const [freshUrls, setFreshUrls] = useState({});
  const touchStartX = useRef(null);
  const hideTimerRef = useRef(null);
  const longPressRef = useRef(null);

  // Кнопку «Ответить» прячем С ЗАДЕРЖКОЙ. Она висит сбоку от пузыря, за его
  // границей, и при движении мыши к ней срабатывал mouseleave — кнопка
  // исчезала раньше, чем до неё удавалось долететь курсором.
  const showActions = () => {
    if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null; }
    setIsHovered(true);
  };
  const hideActions = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setIsHovered(false), 260);
  };
  useEffect(() => () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (longPressRef.current) clearTimeout(longPressRef.current);
  }, []);

  // Если выделение всё же успело начаться (например, палец чуть проехал до
  // срабатывания долгого нажатия) — снимаем его перед открытием меню. Иначе
  // синий блок остаётся висеть под меню и выглядит как мусор.
  const dropSelection = () => {
    try {
      const sel = typeof window !== 'undefined' && window.getSelection?.();
      if (sel && !sel.isCollapsed) sel.removeAllRanges();
    } catch (e) { /* выделения может не быть вовсе */ }
  };

  const openMenu = (e) => {
    if (!onContextMenu) return;
    e.preventDefault();
    e.stopPropagation();
    dropSelection();
    const point = e.touches?.[0] || e.changedTouches?.[0] || e;
    onContextMenu({ x: point.clientX, y: point.clientY }, message);
  };

  // ── Двойное нажатие — сердце, как в мессенджерах ────────────────────────
  // Своя обработка, а не onDoubleClick: на телефоне двойного клика нет,
  // а dblclick синтезируется не всеми браузерами и не по двум тапам.
  const lastTapRef = useRef(0);
  const lastTouchAtRef = useRef(0);
  const longPressFiredRef = useRef(false);
  const [heartBurst, setHeartBurst] = useState(0);   // счётчик — перезапускает анимацию

  const quickLike = () => {
    if (!onReact) return;
    // Реакция переключается: второй двойной тап снимает сердце. Улетающее
    // сердце при СНЯТИИ было бы обманом — показываем его только когда
    // реакция ставится.
    const alreadyMine = (message?.reactions || [])
      .some((r) => r.emoji === QUICK_REACTION && r.mine);
    onReact(message, QUICK_REACTION);
    if (!alreadyMine) setHeartBurst((n) => n + 1);
  };

  const handleTapForLike = () => {
    const now = Date.now();
    // `lastTapRef.current > 0` — не формальность: ноль здесь означает «первого
    // тапа ещё не было», и без проверки первое же касание считалось бы вторым.
    const isDouble = lastTapRef.current > 0 && now - lastTapRef.current < 320;
    lastTapRef.current = isDouble ? 0 : now;
    if (isDouble) quickLike();
    return isDouble;
  };

  // Мобильные браузеры досылают после тапа синтетические mouse-события,
  // среди них dblclick. Без этой заслонки двойной тап срабатывал дважды:
  // реакция ставилась и тут же снималась, а человек видел два сердца и
  // пустой пузырь — «не работает».
  const handleDoubleClick = (e) => {
    if (Date.now() - lastTouchAtRef.current < 700) return;
    e.preventDefault();
    quickLike();
  };

  const time = bubbleTime(message?.date_create);
  const isReadByOthers = (message?.views_count ?? 0) > 0;
  const attachments = message?.attachments || [];
  const hasText = Boolean((message?.content || '').trim());
  const photos = attachments.filter((a) => a?.type === 'photo');
  const voices = attachments.filter((a) => a?.type === 'voice');
  const files = attachments.filter((a) => a?.type !== 'photo' && a?.type !== 'voice');
  const mediaOnly = !hasText && photos.length > 0;

  // ── Подсветка @упоминаний ───────────────────────────────────────────
  const renderContent = (text, mentionsList, isYouMsg) => {
    if (!text) return text;
    const names = (mentionsList || []).map((m) => m.name).filter(Boolean);
    if (names.length === 0) return text;
    const escaped = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const re = new RegExp(`@(${escaped.join('|')})`, 'g');
    const parts = [];
    let last = 0;
    let match;
    while ((match = re.exec(text)) !== null) {
      if (match.index > last) parts.push(text.slice(last, match.index));
      parts.push(
        <Box
          key={match.index}
          component="span"
          sx={{
            fontWeight: 700,
            borderRadius: '4px',
            px: '2px',
            color: isYouMsg ? '#fff' : 'primary.600',
            backgroundColor: isYouMsg ? 'rgba(255,255,255,0.18)' : 'rgba(99,102,241,0.12)',
          }}
        >
          {match[0]}
        </Box>,
      );
      last = match.index + match[0].length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts;
  };

  // Stored event rows keep their line breaks and share the left chat edge.
  if (message?.type_sender === 'system') {
    return (
      <SystemMessage
        content={message?.content}
        time={time}
        dateTime={message?.date_create}
        onContextMenu={onContextMenu ? openMenu : undefined}
        onTouchStart={onContextMenu ? e => {
          const point = { x: e.touches[0].clientX, y: e.touches[0].clientY };
          if (longPressRef.current) clearTimeout(longPressRef.current);
          longPressRef.current = setTimeout(() => { longPressRef.current = null; dropSelection(); onContextMenu(point, message); }, 500);
        } : undefined}
        onTouchMove={() => { if (longPressRef.current) clearTimeout(longPressRef.current); }}
        onTouchEnd={() => { if (longPressRef.current) clearTimeout(longPressRef.current); }}
        onTouchCancel={() => { if (longPressRef.current) clearTimeout(longPressRef.current); }}
      />
    );
  }

  // ── Скругления в зависимости от места в серии ───────────────────────
  const R = CHAT.bubbleRadius;
  const T = CHAT.bubbleTailRadius;
  const radius = (() => {
    if (isYou) {
      return {
        borderTopLeftRadius: R,
        borderBottomLeftRadius: R,
        borderTopRightRadius: groupPos === 'first' || groupPos === 'single' ? R : T,
        borderBottomRightRadius: groupPos === 'last' || groupPos === 'single' ? T : T,
      };
    }
    return {
      borderTopRightRadius: R,
      borderBottomRightRadius: R,
      borderTopLeftRadius: groupPos === 'first' || groupPos === 'single' ? R : T,
      borderBottomLeftRadius: groupPos === 'last' || groupPos === 'single' ? T : T,
    };
  })();

  // ── Вложения ────────────────────────────────────────────────────────
  const renderPhotos = () => {
    if (photos.length === 0) return null;
    const single = photos.length === 1;
    return (
      <Box
        sx={{
          mt: hasText ? 0.75 : 0,
          mx: mediaOnly ? `-${CHAT.bubblePadX}px` : 0,
          mb: mediaOnly ? `-${CHAT.bubblePadY}px` : 0,
          display: 'grid',
          gap: '3px',
          gridTemplateColumns: single ? '1fr' : 'repeat(2, 1fr)',
          borderRadius: mediaOnly ? `${R}px` : '12px',
          overflow: 'hidden',
        }}
      >
        {photos.slice(0, 4).map((att, i) => (
          <Box
            key={att.id ?? i}
            // Открываем свой просмотрщик, а не новую вкладку: из вкладки
            // человек возвращался в чат и заново искал место в переписке.
            onClick={() => setViewerAt(i)}
            sx={{
              position: 'relative',
              cursor: 'pointer',
              overflow: 'hidden',
              lineHeight: 0,
              backgroundColor: 'rgba(0,0,0,0.06)',
              aspectRatio: single ? 'auto' : '1 / 1',
              '&:hover img': { transform: 'scale(1.03)' },
            }}
          >
            <ChatMediaPhoto attachment={att} chatId={message.chat_id} messageId={message.id} single={single}
              onOpen={url => { setFreshUrls(prev => ({ ...prev, [att.id]: url })); setViewerAt(i); }} />
            {photos.length > 4 && i === 3 && (
              <Box
                sx={{
                  position: 'absolute', inset: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.5)', color: '#fff',
                  fontSize: 22, fontWeight: 700,
                }}
              >
                +{photos.length - 3}
              </Box>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  const renderFiles = () => files.map((att, i) => (
    <Box
      key={att.id ?? i}
      sx={{
        mt: 0.75,
        display: 'flex', alignItems: 'center', gap: 1,
        px: 1, py: 0.75,
        borderRadius: '12px',
        backgroundColor: isYou ? 'rgba(255,255,255,0.14)' : 'rgba(22,34,71,0.05)',
      }}
    >
      <Box
        sx={{
          width: 32, height: 32, borderRadius: '10px', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: isYou ? 'rgba(255,255,255,0.2)' : 'rgba(99,102,241,0.12)',
        }}
      >
        <InsertDriveFileRoundedIcon
          sx={{ fontSize: 18, color: isYou ? '#fff' : 'primary.500' }}
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          level="body-xs"
          noWrap
          sx={{ fontWeight: 600, color: isYou ? '#fff' : 'text.primary' }}
        >
          {att.name}
        </Typography>
      </Box>
      <IconButton
        size="sm"
        variant="plain"
        onClick={async () => { setMediaError(''); try { await openChatAttachment(att, message.chat_id, message.id); } catch (error) { setMediaError(error.message); } }}
        title="Скачать файл"
        sx={{
          '--IconButton-size': '28px',
          ...iconColor(isYou ? '#fff' : 'var(--joy-palette-text-secondary)'),
          '&:hover': { backgroundColor: isYou ? 'rgba(255,255,255,0.18)' : undefined },
        }}
      >
        <DownloadRoundedIcon sx={{ fontSize: 17 }} />
      </IconButton>
    </Box>
  ));

  const renderVoices = () => voices.map((att) => (
    <Box key={att.id} sx={{ mt: hasText ? 0.75 : 0 }}>
      <VoiceMessage
        attachment={att}
        chatId={message.chat_id}
        messageId={message.id}
        isYou={isYou}
      />
    </Box>
  ));

  // Метка времени: внутри пузыря или поверх картинки
  const timeStamp = (
    <Box component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: '3px',
        fontSize: 10.5, lineHeight: 1,
        whiteSpace: 'nowrap',
        color: isYou ? 'rgba(255,255,255,0.78)' : 'text.tertiary',
        ...(mediaOnly && {
          px: 0.75, py: 0.375, borderRadius: 10,
          backgroundColor: 'rgba(0,0,0,0.45)',
          color: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(4px)',
        }),
      }}
    >
      {/* Закреплённое помечаем в самой ленте: иначе, дойдя до него
          прокруткой, не понять, почему оно же висит наверху. */}
      {message?.is_pinned && (
        <PushPinRoundedIcon sx={{ fontSize: 11, opacity: 0.85, transform: 'rotate(35deg)' }} />
      )}
      {message?.is_edited && (
        <Box component="span" sx={{ opacity: 0.8, fontStyle: 'italic' }}>изм.</Box>
      )}
      {time}
      {isYou && (
        // Одна галочка — доставлено, две — кто-то уже прочитал.
        // Раньше двойная галочка стояла всегда и ничего не значила.
        isReadByOthers
          ? <DoneAllRoundedIcon sx={{ fontSize: 13, opacity: 0.95 }} />
          : <DoneRoundedIcon sx={{ fontSize: 13, opacity: 0.7 }} />
      )}
    </Box>
  );

  // ── Реакции под пузырём ─────────────────────────────────────────────
  const reactions = message?.reactions || [];
  const reactionsRow = reactions.length > 0 && (
    <Box
      sx={{
        display: 'flex', flexWrap: 'wrap', gap: 0.5,
        mt: 0.5,
        justifyContent: isYou ? 'flex-end' : 'flex-start',
      }}
    >
      {reactions.map((r) => (
        <Box
          key={r.emoji}
          component={motion.button}
          type="button"
          title={(r.names || []).join(', ')}
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={MOTION.spring}
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => onReact?.(message, r.emoji)}
          sx={(theme) => ({
            display: 'inline-flex', alignItems: 'center', gap: 0.375,
            px: 0.75, py: 0.25, borderRadius: 10,
            cursor: 'pointer', fontSize: 13, lineHeight: 1.4,
            border: '1px solid',
            borderColor: r.mine ? 'primary.400' : 'transparent',
            backgroundColor: r.mine
              ? 'rgba(99,102,241,0.16)'
              : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(22,34,71,0.06)'),
            color: 'text.secondary',
            transition: 'background-color .15s',
          })}
        >
          <Box component="span">{r.emoji}</Box>
          {r.count > 1 && (
            <Box component="span" sx={{ fontSize: 11, fontWeight: 700, color: r.mine ? 'primary.600' : 'text.tertiary' }}>
              {r.count}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );

  return (
    <Box
      component={motion.div}
      initial={false}
      animate={{ opacity: 1 }}
      sx={{
        maxWidth: CHAT.bubbleMaxWidth,
        minWidth: 0,
        alignSelf: isYou ? 'flex-end' : 'flex-start',
        position: 'relative',
        transform: dragX ? `translateX(${dragX}px)` : 'none',
        transition: touchStartX.current == null ? 'transform .15s' : 'none',
        // На сенсорных экранах долгое нажатие — это наш жест вызова меню, а
        // браузер трактует его по-своему: начинает выделять текст и поднимает
        // системную панель «Скопировать / Выделить всё». Она перехватывала
        // касания, и по пунктам нашего меню было не попасть.
        //
        // Запрещаем выделение ТОЛЬКО там, где нет мыши: на компьютере текст
        // выделять надо, это обычный способ его скопировать. На телефоне для
        // этого есть пункт «Копировать текст».
        '@media (hover: none) and (pointer: coarse)': {
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
        },
      }}
      onMouseEnter={showActions}
      onMouseLeave={hideActions}
      onContextMenu={openMenu}
      onDoubleClick={handleDoubleClick}
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0].clientX;
        lastTouchAtRef.current = Date.now();
        longPressFiredRef.current = false;
        // Долгое нажатие (500 мс) открывает то же меню, что правая кнопка
        const pt = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        if (longPressRef.current) clearTimeout(longPressRef.current);
        longPressRef.current = setTimeout(() => {
          longPressFiredRef.current = true;
          longPressRef.current = null;
          dropSelection();
          onContextMenu?.(pt, message);
          touchStartX.current = null;
        }, 500);
      }}
      onTouchMove={(e) => {
        // Палец поехал — это свайп для ответа, а не долгое нажатие
        if (longPressRef.current) { clearTimeout(longPressRef.current); longPressRef.current = null; }
        if (touchStartX.current == null) return;
        const dx = e.touches[0].clientX - touchStartX.current;
        if (dx > 0) setDragX(Math.min(dx, 64));
      }}
      onTouchEnd={() => {
        // Флагом, а не «таймер уже null»: сам таймер обнуляется и при
        // обычном тапе тоже, и отличить одно от другого по нему нельзя.
        // Из-за этого после долгого нажатия отпускание пальца считалось
        // тапом, и закрытие меню вторым касанием ставило сердце.
        const wasLongPress = longPressFiredRef.current;
        lastTouchAtRef.current = Date.now();
        if (longPressRef.current) { clearTimeout(longPressRef.current); longPressRef.current = null; }
        if (dragX > 44) {
          onReply?.(message);
        } else if (dragX === 0 && !wasLongPress) {
          // Тап без свайпа и без долгого нажатия — кандидат в двойной.
          handleTapForLike();
        }
        setDragX(0);
        touchStartX.current = null;
        longPressFiredRef.current = false;
      }}
    >
      {/* Сердце вылетает вверх и тает — подтверждение, что реакция ушла.
          key на счётчике: без него повторный двойной тап не перезапустил бы
          анимацию, и человек решил бы, что нажатие не сработало. */}
      <AnimatePresence>
        {heartBurst > 0 && (
          <motion.div
            key={heartBurst}
            initial={{ opacity: 0, scale: 0.4, y: 0 }}
            animate={{ opacity: [0, 1, 1, 0], scale: [0.4, 1.35, 1.1, 0.9], y: -46 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
            onAnimationComplete={() => setHeartBurst(0)}
            style={{
              position: 'absolute', top: '50%', left: '50%',
              marginLeft: -14, marginTop: -14,
              fontSize: 28, lineHeight: 1, pointerEvents: 'none', zIndex: 5,
            }}
          >
            {QUICK_REACTION}
          </motion.div>
        )}
      </AnimatePresence>
      <Box
        sx={(theme) => ({
          position: 'relative',
          px: `${CHAT.bubblePadX}px`,
          py: `${CHAT.bubblePadY}px`,
          ...radius,
          ...(isYou ? ownBubble(theme) : otherBubble(theme)),
          // «Хвостик» делаем скруглениями, а не треугольником-псевдоэлементом:
          // у своего пузыря фон градиентный, и приклеенный треугольник плоского
          // цвета дал бы заметный шов. Острый угол у последнего сообщения серии
          // читается как хвост и рисуется абсолютно надёжно в любом браузере.
        })}
      >
        {/* Имя отправителя — только в группах и только в начале серии */}
        {showName && !isYou && (
          <Typography
            level="body-xs"
            noWrap
            sx={{ fontWeight: 700, mb: 0.25, color: 'primary.500', fontSize: 12 }}
          >
            {personName(message?.sender, 'Участник')}
          </Typography>
        )}

        {/* Цитата */}
        {message?.reply_to && (
          <Box
            sx={{
              mb: 0.625,
              px: 1, py: 0.5,
              borderRadius: '8px',
              borderLeft: '3px solid',
              borderColor: isYou ? 'rgba(255,255,255,0.65)' : 'primary.400',
              backgroundColor: isYou ? 'rgba(255,255,255,0.13)' : 'rgba(99,102,241,0.08)',
            }}
          >
            <Typography
              level="body-xs"
              noWrap
              sx={{ fontWeight: 700, fontSize: 11.5, color: isYou ? '#fff' : 'primary.600' }}
            >
              {message.reply_to.sender_name || 'Сообщение'}
            </Typography>
            <Typography
              level="body-xs"
              noWrap
              sx={{ fontSize: 11.5, color: isYou ? 'rgba(255,255,255,0.82)' : 'text.secondary' }}
            >
              {message.reply_to.content || 'вложение'}
            </Typography>
          </Box>
        )}

        {renderPhotos()}
        {renderVoices()}

        {/* Текст + «дырка» под время в правом нижнем углу */}
        {hasText && (
          <Typography
            level="body-sm"
            sx={{
              position: 'relative',
              fontSize: 14,
              lineHeight: 1.42,
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              color: isYou ? '#fff' : 'text.primary',
            }}
          >
            {renderContent(message?.content, message?.mentions, isYou)}
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: timeSlotWidth({
                  isYou,
                  isEdited: Boolean(message?.is_edited),
                  isPinned: Boolean(message?.is_pinned),
                }),
                height: 1,
              }}
            />
            <Box component="span" sx={{ position: 'absolute', right: 0, bottom: 1 }}>
              {timeStamp}
            </Box>
          </Typography>
        )}

        {renderFiles()}
        {mediaError && <Typography level="body-xs" sx={{ color: 'danger.600', mt: 1 }}>{mediaError}</Typography>}

        {/* Время для сообщений без текста */}
        {!hasText && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              ...(mediaOnly
                ? { position: 'absolute', right: 8, bottom: 8 }
                : { mt: 0.5 }),
            }}
          >
            {timeStamp}
          </Box>
        )}

        {/* «Ответить» при наведении */}
        {isHovered && (
          <Tooltip title="Ответить" size="sm" placement={isYou ? 'left' : 'right'}>
            <IconButton
              component={motion.button}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: MOTION.fast }}
              variant="plain"
              onMouseEnter={showActions}
              onMouseLeave={hideActions}
              onClick={() => onReply?.(message)}
              sx={(theme) => ({
                position: 'absolute',
                bottom: 2,
                zIndex: 2,
                right: isYou ? 'auto' : -32,
                left: isYou ? -32 : 'auto',
                '--IconButton-size': '28px',
                borderRadius: '50%',
                backgroundColor: theme.palette.mode === 'dark' ? '#2b3145' : '#fff',
                boxShadow: 'sm',
                // Иконку красим через --Icon-color: обычный color её не берёт,
                // из-за чего значок оставался чёрным.
                ...iconColor(theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.72)' : 'rgba(71,84,103,0.75)'),
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? '#343b52' : '#f4f6fb',
                  ...iconColor('var(--joy-palette-primary-500)'),
                },
              })}
            >
              <ReplyRoundedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {reactionsRow}

      {/* Просмотрщик фотографий этого сообщения */}
      {viewerAt !== null && photos.length > 0 && (
        <ImageViewer
          images={photos.map((p) => ({
            id: p.id,
            url: freshUrls[p.id] || chatMediaUrl(p.url, API_URL),
            refresh: () => freshChatAttachment(p, message.chat_id, message.id),
            name: p.name,
          }))}
          startIndex={Math.min(viewerAt, photos.length - 1)}
          onClose={() => setViewerAt(null)}
        />
      )}
    </Box>
  );
}
